import { DialogContent, Grid2, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import { SlideoutPanelDialog } from "../../../SlideoutPanel/SlideoutPanelDialog.component";
import { CustomColoredTabs } from "../../../CustomColoredTabs/CustomColoredTabs.component";
import { CustomColoredTab } from "../../../CustomColoredTabs/CustomColoredTab.component";
import { a11yProps } from "../../../../features/TabPanel/TabPanel.partial";
import { TabPanel } from "../../../../features/TabPanel/TabPanel.component";
import { WeekdaysSelector } from "../DateAndTypeSelector/weekdays-selector.component";
import {
  SelectedZoneOrWorkplaceType,
  setFilterSelected,
  setIsSelected,
  setSelectedByAttFilter,
  setSelectedZoneOrWorkplace
} from "../../../../features/Reports/slices/report.slice";
import { DepartmentSelector2 as DepartmentSelector } from "../DateAndTypeSelector/department-selector(2).component";
import { AttributeSelector } from "../DateAndTypeSelector/attribute-selector.component";
import { CapacityObject, FloorPlanMemo } from "../../../../features/Reports/typings/reports.types";
import { FilterTypState } from "../DateAndTypeSelector/facility-report-filter-and-selector.component";
import { IPlaceSchedule } from "../../../FacilityManager/Domain/Types/FloorPlan/PlaceSchedule";
import { IZoneSchedule } from "../../../FacilityManager/Domain/Types/FloorPlan/ZoneSchedule";
import { fetchReport } from "../../../../features/Reports/thunks/report.thunk";
import { useRemoteFetchUserInfoForAllUsers } from "../../../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { handleInitialDepartmentOptions } from "../../functions/handleInitialDepartmentOption.function";
import { initialWeekdays } from "../../../../features/Reports/initialWeekdays.constant";
import {
  getFetchValues,
  getFilterAndBookingType,
  getFilterEntries,
  getFilterParams,
  handleDispatchSelectedByAttFilter
} from "./functions/handleGroupFilterCalculate.functions";
import { GroupFilterSelected } from "../../../BookingForm/BookingFilter/types/GroupFilters.type";

export interface FilteredSelectables {
  [key: string]: {
    floorInventoryId: number;
    entries: (IPlaceSchedule | IZoneSchedule)[] | undefined;
  };
}

type P = {
  isOpen: boolean;
  setIsOpen: (o: boolean) => void;
  setFetchEnabled: (e: boolean) => void;
  isHrAdmin: boolean;
  floorData?: FloorPlanMemo;
  filterType: FilterTypState[];
  basicParams: any;
  entityData: (CapacityObject | undefined)[];
  /**
   * When true, Calculate/Reset still update Redux filters and floor-plan highlight,
   * but skip `fetchReport` (caller refetches, e.g. Management React Query).
   */
  skipReportFetch?: boolean;
  /** Hide Weekday tab when the host already exposes a weekdays control (e.g. Management toolbar). */
  hideWeekdayTab?: boolean;
};

export function FilterSlideoutDialog({
  isOpen,
  setIsOpen,
  setFetchEnabled,
  isHrAdmin,
  floorData,
  filterType,
  basicParams,
  entityData,
  skipReportFetch = false,
  hideWeekdayTab = false
}: Readonly<P>) {
  const { t } = useTranslation();

  const theme = useTheme();
  const isMobileTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const {
    currentFloors,
    selectedZoneOrWorkplace,
    selectedBookingType,
    filterSelected: { selectedWeekdays, selectedDepartmentIds }
  } = useSelector((state: RootState) => state.report);
  const dispatch = useDispatch();

  const tableSxStyle = {
    fontSize: isMobileTablet ? 9 : 13,
    fontWeight: theme.typography.fontWeightRegular,
    pl: isMobileTablet ? 0 : 2,
    pr: isMobileTablet ? 0 : 2,
    "&.Mui-selected": {
      color:
        theme.palette.mode === "dark"
          ? theme.palette.getContrastText(theme.palette.primary.main)
          : theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold
    }
  };

  const { data: remoteUsers } = useRemoteFetchUserInfoForAllUsers();

  const [selectTab, setSelectTab] = useState(0);

  /** temporary store the department filter item selection */
  const [departmentSelected, setDepartmentSelected] = useState<{ id: string; name: string }[]>([]);
  /** temporary store the attribute filter item selection */
  const [attributeSelected, setAttributeSelected] = useState<GroupFilterSelected[]>([]);
  /** filtered entries(place or zone) by attributes and store them in the different floor inventory */
  const [filteredSelectables, setFilteredSelectables] = useState<FilteredSelectables>({});
  /** disable calculate when no entries by attributes filter existed */
  const [disableCalculate, setDisableCalculate] = useState(false);

  // department options from the entity data distinctTotalUsers
  const departmentOpts = handleInitialDepartmentOptions({
    remoteUsers,
    entityData,
    selectedDepartmentIds,
    t
  });

  const handleChange = (e: any, newValue: number) => setSelectTab(newValue);
  const handleClose = () => {
    setSelectTab(0);
    setIsOpen(false);
  };

  const handleGroupFilterCalculate = (isCalculateOrReset: boolean) => {
    // later, it needs to refactor, currently somehow it affects entityData so every time department selection changes,
    // it triggers the entityData changes so selection doesn't work well
    dispatch(
      setFilterSelected({
        selectedDepartmentIds: departmentSelected.map(sel => sel.id),
        selectedAttributes: attributeSelected
      })
    );

    currentFloors.forEach((floor, index) => {
      const attFilterEntries = filteredSelectables[floor.id]?.entries || [];
      const { params: filterParams, reportMode } = getFilterParams({
        selectedZoneOrWorkplace,
        selectedBookingType,
        floor,
        floorData: floorData ? floorData[floor.id].floorData : undefined,
        attFilterEntries,
        attributeSelected
      });

      const newAttFilterEntries = getFilterEntries(
        selectedZoneOrWorkplace,
        attFilterEntries,
        floorData,
        floor
      );

      const { filterTyp, bookingType } = getFilterAndBookingType(
        newAttFilterEntries,
        selectedBookingType,
        filterType[index]
      );

      const { calcPerWpVal, filterTypeVal, isAttFilterAppliedVal, fetchReportId } = getFetchValues(
        newAttFilterEntries,
        floor,
        selectedZoneOrWorkplace,
        reportMode,
        filterTyp
      );

      if (filterParams.length && isCalculateOrReset) {
        if (!skipReportFetch) {
          dispatch(
            fetchReport({
              ...basicParams,
              id: fetchReportId,
              calcPerWorkplace: calcPerWpVal,
              filterType: filterTypeVal,
              filter: filterParams,
              bookingType,
              weekdays: selectedWeekdays,
              departmentIds: departmentSelected.map(sel => sel.id),
              isAttFilterApplied: {
                floorInventoryId: floor.id,
                applied: isAttFilterAppliedVal
              }
            })
          );
        }

        handleDispatchSelectedByAttFilter(
          newAttFilterEntries,
          dispatch,
          filterParams,
          bookingType,
          floor.id
        );
      }
    });

    // close the filter dialog
    handleClose();
    setFetchEnabled(false);
  };

  const handleGroupFilterReset = () => {
    // select the initial state and empty selection
    dispatch(
      setFilterSelected({
        ...(hideWeekdayTab ? {} : { selectedWeekdays: initialWeekdays }),
        selectedAttributes: [],
        selectedDepartmentIds: []
      })
    );

    dispatch(setIsSelected(false));
    dispatch(
      setSelectedZoneOrWorkplace({
        id: [],
        name: [],
        type: SelectedZoneOrWorkplaceType.ZONE,
        open: false,
        floorInventoryId: 0,
        bookingType: undefined
      })
    );

    dispatch(
      setSelectedByAttFilter({
        id: [],
        name: [],
        type: SelectedZoneOrWorkplaceType.ZONE,
        open: false,
        floorInventoryId: -1,
        bookingType: undefined
      })
    );

    setDepartmentSelected([]);
    setFilteredSelectables({});

    // close the filter dialog
    handleClose();

    if (skipReportFetch) {
      return;
    }

    // refetch with empty filter items
    currentFloors.forEach(floor => {
      dispatch(
        fetchReport({
          ...basicParams,
          id: floor.id,
          calcPerWorkplace: true,
          filterType: "floor",
          filter: [floor.id],
          bookingType: selectedBookingType,
          weekdays: initialWeekdays,
          departmentIds: []
        })
      );
    });
  };

  /* istanbul ignore next */
  useEffect(() => {
    const isEntryEmpty = Object.values(filteredSelectables).some(
      item => item.entries && item.entries.length > 0
    );

    const noFulfilledResultByAttFilter =
      attributeSelected && attributeSelected?.length > 0 && !isEntryEmpty;

    // check if no filfilled result exists by attributes filter
    if (noFulfilledResultByAttFilter) return setDisableCalculate(true);
    else setDisableCalculate(false);
  }, [filteredSelectables]);

  const showWeekdayTab = !hideWeekdayTab;
  let tabIndex = 0;
  const weekdayTabIndex = showWeekdayTab ? tabIndex++ : -1;
  const departmentsTabIndex = isHrAdmin ? tabIndex++ : -1;
  const attributesTabIndex = floorData ? tabIndex++ : -1;

  return (
    <SlideoutPanelDialog
      isOpen={isOpen}
      dialogTitle={t("Filter")}
      width={"25vw"}
      height="0px" // calc(100vh - height)
      showCloseButton
      onClose={handleClose}
      primaryButtonTitle={t("Calculate")}
      secondaryButtonTitle={t("Reset")}
      primaryButtonAction={() => /* istanbul ignore next */ handleGroupFilterCalculate(true)}
      secondaryButtonAction={() => /* istanbul ignore next */ handleGroupFilterReset()}
      disablePrimaryButton={disableCalculate}
      disablePrimaryTooltip={t("No result for selected filters")}
    >
      {/* contents of filter slideout panel */}
      <Grid2 container data-testid="booking-selection-dialog-contents-tabs" mt={1.5}>
        <CustomColoredTabs value={selectTab} onChange={handleChange} autoScrollable>
          {showWeekdayTab ? (
            <CustomColoredTab
              index={weekdayTabIndex}
              data-testid={"capacity-filter-tab0"}
              label={t("Weekday")}
              sx={tableSxStyle}
              {...a11yProps(weekdayTabIndex)}
            />
          ) : null}

          {isHrAdmin && (
            <CustomColoredTab
              index={departmentsTabIndex}
              data-testid={"capacity-filter-tab1"}
              label={t("Departments")}
              sx={tableSxStyle}
              {...a11yProps(departmentsTabIndex)}
            />
          )}

          {floorData && (
            <CustomColoredTab
              index={attributesTabIndex}
              data-testid={"capacity-filter-tab2"}
              sx={tableSxStyle}
              label={t("Attributes")}
              {...a11yProps(attributesTabIndex)}
            />
          )}
        </CustomColoredTabs>
      </Grid2>

      {/* dialog content */}
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          padding: 0,
          overflowY: "visible"
        }}
      >
        <Grid2 container data-testid="capacity-filter-dialog-contents">
          {/* tabPanel - weekdays filter */}
          {showWeekdayTab ? (
            <TabPanel value={selectTab} index={weekdayTabIndex}>
              <Grid2 container>
                <WeekdaysSelector
                  weekdays={selectedWeekdays ?? initialWeekdays}
                  setWeekdaysToggle={weekdays => {
                    dispatch(setFilterSelected({ selectedWeekdays: weekdays }));
                  }}
                  onEnableFetch={() => setFetchEnabled(true)}
                />
              </Grid2>
            </TabPanel>
          ) : null}

          {/* tabPanel - department filter */}
          {isHrAdmin ? (
            <TabPanel value={selectTab} index={departmentsTabIndex}>
              <Grid2 container>
                <DepartmentSelector
                  departmentData={departmentOpts}
                  departmentSelected={departmentSelected}
                  selectedDepartmentIds={selectedDepartmentIds}
                  handleDepartmentSelect={(e: any, value: { id: string; name: string }[]) =>
                    setDepartmentSelected(value)
                  }
                />
              </Grid2>
            </TabPanel>
          ) : null}

          {/* tabPanel - attribute filter */}
          {floorData && (
            <TabPanel value={selectTab} index={attributesTabIndex}>
              <Grid2 container sx={{ px: 1 }}>
                <AttributeSelector
                  floorData={floorData}
                  filterType={filterType}
                  filteredSelectables={filteredSelectables}
                  setFilteredSelectables={setFilteredSelectables}
                  setAttributeSelected={setAttributeSelected}
                />
              </Grid2>
            </TabPanel>
          )}
        </Grid2>
      </DialogContent>
    </SlideoutPanelDialog>
  );
}

/** working version - draft */
/**
 * currentFloors.forEach((floor, index) => {
  let filterTyp: FilterTypState = "floor";
  let filterParams: number[];
  let bookingType = selectedBookingType;
  let attFilterEntries: (IPlaceSchedule | IZoneSchedule)[] = [];

  if (index === 0) {
    filterTyp = filterType[0];
    filterParams =
      selectedZoneOrWorkplace &&
      selectedZoneOrWorkplace.open &&
      selectedZoneOrWorkplace.floorInventoryId === currentFloors[0].id
        ? selectedZoneOrWorkplace.id
        : [currentFloors[0]?.id];

    // apply attribute filter
    attFilterEntries =
      (filteredSelectables[currentFloors[0].id] &&
        filteredSelectables[currentFloors[0].id].entries) ||
      [];
    if (attFilterEntries.length) {
      filterParams = attFilterEntries.map(ent => ent.inventoryId || ent.inventory?.id || 0);

      if (selectedBookingType !== BookingType.CONFERENCEZONE) {
        const types = (attFilterEntries as IPlaceSchedule[]).map(
          p => p.inventory?.placeTypeId || 0
        );
        bookingType = findPlaceBookingType(types[0]);
        filterTyp = SelectedZoneOrWorkplaceType.WORKPLACE;
      } else {
        const types = (attFilterEntries as IZoneSchedule[]).map(
          p => p.inventory?.zoneTypeId || 0
        );
        bookingType = findZoneBookingType(types[0]);
        filterTyp = SelectedZoneOrWorkplaceType.ZONE;
      }
    }
  } else {
    filterTyp = filterType[1];
    filterParams =
      selectedZoneOrWorkplace &&
      selectedZoneOrWorkplace.open &&
      currentFloors.length > 1 &&
      selectedZoneOrWorkplace.floorInventoryId === currentFloors[1].id
        ? selectedZoneOrWorkplace.id
        : [currentFloors[1]?.id];

    // apply attribute filter
    attFilterEntries =
      (filteredSelectables[currentFloors[1].id] &&
        filteredSelectables[currentFloors[1].id].entries) ||
      [];
    if (attFilterEntries.length) {
      filterParams = attFilterEntries.map(ent => ent.inventoryId || ent.inventory?.id || 0);
      if (selectedBookingType !== BookingType.CONFERENCEZONE) {
        const types = (attFilterEntries as IPlaceSchedule[]).map(
          p => p.inventory?.placeTypeId || 0
        );
        bookingType = findPlaceBookingType(types[0]);
        filterTyp = SelectedZoneOrWorkplaceType.WORKPLACE;
      } else {
        const types = (attFilterEntries as IZoneSchedule[]).map(
          p => p.inventory?.zoneTypeId || 0
        );
        bookingType = findZoneBookingType(types[0]);
        filterTyp = SelectedZoneOrWorkplaceType.ZONE;
      }
    }
  }

  if (filterParams.length && isCalculateOrReset) {
    dispatch(
      fetchReport({
        ...basicParams,
        calcPerWorkplace: false,
        filterType: isCalculateOrReset ? filterTyp : "floor",
        filter: isCalculateOrReset ? filterParams : [floor.id],
        bookingType: bookingType,
        weekdays: selectedWeekdays,
        departmentIds: isCalculateOrReset ? departmentSelected.map(sel => sel.id) : [],
        isAttFilterApplied:
          isCalculateOrReset && filterParams.length
            ? {
                floorInventoryId: currentFloors[index].id,
                applied: true
              }
            : undefined
      })
    );

    dispatch(
      setSelectedByAttFilter({
        id: filterParams,
        name: attFilterEntries.map(ent => ent.inventory?.name || ""),
        open: true,
        type:
          bookingType !== BookingType.CONFERENCEZONE
            ? SelectedZoneOrWorkplaceType.WORKPLACE
            : SelectedZoneOrWorkplaceType.ZONE,
        floorInventoryId: currentFloors[index].id,
        bookingType: bookingType || BookingType.WORKPLACE
      })
    );
  }
}); */
